import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { handleError, ERROR_CATEGORY } from '../../../utils/ErrorUtils';

// Stable constant — defined outside the component so it never triggers dependency changes.
// date_of_change: 2026-05-21
const DRAWING_TOOLS = {
  FREEHAND: 'freehand', // date_of_change: 2026-05-24 - Now works like fog lasso (smooth polygon with live preview)
  LINE: 'line',
  SQUARE: 'square',
  CIRCLE: 'circle',
  TEXT: 'text',
  POLYGON: 'polygon',
  ERASER: 'eraser'
};

/**
 * Drawing Canvas Component for Roll20-like drawing functionality
 * Provides a canvas overlay for drawing on top of the map
 * Supports multiple drawing tools: freehand, line, square, circle, text, polygon, eraser
 */
const DrawingCanvas = ({
  enabled = false,
  color = '#ff0000',
  lineWidth = 2,
  mapRef = null,
  zoom = 1,
  mapPosition = { x: 0, y: 0 },
  onDrawingComplete = null,
  // GAP-002: controlled drawings prop — when provided, replaces internal state for persistence
  // date_of_change: 2026-05-20
  drawings: externalDrawings = null,
  clearOnDisable = false,
  snapToGrid = false,
  gridSize = 50,
  drawingTool = 'freehand', // freehand, line, square, circle, text, polygon, eraser
  currentText = ''
}) => {
  // Canvas references
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  // When externalDrawings prop is provided, use it as the source of truth (controlled mode)
  // date_of_change: 2026-05-20
  const [internalDrawings, setInternalDrawings] = useState([]);
  const drawings = externalDrawings !== null ? externalDrawings : internalDrawings;
  const setDrawings = useCallback((updater) => {
    if (externalDrawings !== null) {
      // Controlled: push new drawing up via onDrawingComplete
      return;
    }
    setInternalDrawings(updater);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDrawings]);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  
  // Track the last position for drawing
  const lastPosition = useRef({ x: 0, y: 0 });
  // Accumulates eraser circle centres during a drag stroke — committed to drawings on mouseup
  // date_of_change: 2026-05-25
  const currentEraserPoints = useRef([]);
  
  
  // Initialize the canvas context
  useEffect(() => {
    try {
      if (!canvasRef.current) return;
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (!context) {
        handleError(
          new Error('Could not get 2D context from canvas'),
          false,
          { category: ERROR_CATEGORY.GENERAL }
        );
        return;
      }
      
      context.scale(1, 1);
      context.lineCap = 'round';
      context.strokeStyle = color;
      context.lineWidth = lineWidth;
      contextRef.current = context;
      
      // Set canvas dimensions based on parent container
      if (mapRef && mapRef.current) {
        const { width, height } = mapRef.current.getBoundingClientRect();
        canvas.width = width;
        canvas.height = height;
        setCanvasSize({ width, height });
      }
    } catch (error) {
      handleError(
        error,
        true,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Failed to initialize drawing canvas'
        }
      );
    }
  }, [mapRef, color, lineWidth]);
  
  // Update canvas size when map size changes
  useEffect(() => {
    try {
      if (!mapRef || !mapRef.current || !canvasRef.current) return;
      
      const updateCanvasSize = () => {
        const { width, height } = mapRef.current.getBoundingClientRect();
        if (width !== canvasSize.width || height !== canvasSize.height) {
          canvasRef.current.width = width;
          canvasRef.current.height = height;
          setCanvasSize({ width, height });
          // Redraw all paths after resize
          redrawAllPaths();
        }
      };
      
      // Initial update
      updateCanvasSize();
      
      // Add resize observer
      const resizeObserver = new ResizeObserver(updateCanvasSize);
      const observedNode = mapRef.current; // capture ref for cleanup — date_of_change: 2026-05-21
      resizeObserver.observe(observedNode);
      
      return () => {
        if (resizeObserver && observedNode) {
          resizeObserver.unobserve(observedNode);
        }
      };
    } catch (error) {
      handleError(
        error,
        false,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Failed to update canvas size'
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- redrawAllPaths defined after this effect; called inline, no stale closure risk
  }, [mapRef, canvasSize.width, canvasSize.height]);
  
  // Update context properties when they change
  useEffect(() => {
    if (contextRef.current) {
      contextRef.current.strokeStyle = color;
      contextRef.current.lineWidth = lineWidth;
    }
  }, [color, lineWidth]);
  
  // Clear canvas when disabled (if clearOnDisable is true)
  useEffect(() => {
    if (!enabled && clearOnDisable) {
      clearCanvas();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- clearCanvas identity intentionally excluded to avoid loop
  }, [enabled, clearOnDisable]);
  
  // Clear the canvas
  const clearCanvas = useCallback(() => {
    try {
      if (!contextRef.current || !canvasRef.current) return;
      
      contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setDrawings([]);
      setCurrentPath([]);
    } catch (error) {
      handleError(
        error,
        true,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Failed to clear drawing canvas'
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setDrawings is stable callback ref
  }, [setDrawings]);
  
  // Redraw all existing paths
  // date_of_change: 2026-05-22 — dispatch by drawing.type so freehand, shapes, and eraser all
  // replay correctly. Previously every drawing was treated as a polyline (points array), which
  // crashed on shape drawings that only store startPoint/endPoint (TypeError: a is not a function
  // in minified build) and caused eraser strokes to be ignored entirely.
  const redrawAllPaths = useCallback(() => {
    try {
      if (!contextRef.current || !canvasRef.current) return;

      const ctx = contextRef.current;

      // Clear canvas
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Translate a logical coordinate to canvas pixel space
      // date_of_change: 2026-05-24 — formula is (world * zoom) + offset, NOT (world + offset) * zoom
      const adj = (point) => ({
        x: point.x * zoom + mapPosition.x,
        y: point.y * zoom + mapPosition.y
      });

      drawings.forEach((drawing) => {
        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = drawing.color || color;
        ctx.lineWidth  = drawing.lineWidth || lineWidth;
        ctx.lineCap    = 'round';

        if (drawing.type === DRAWING_TOOLS.FREEHAND) {
          // Freehand: open line (not closed polygon)
          // date_of_change: 2026-05-25 - Freehand draws open line, not closed polygon
          if (!drawing.points || drawing.points.length < 2) { ctx.restore(); return; }
          const first = adj(drawing.points[0]);
          ctx.moveTo(first.x, first.y);
          for (let i = 1; i < drawing.points.length; i++) {
            const p = adj(drawing.points[i]);
            ctx.lineTo(p.x, p.y);
          }
          // Open line - no closing back to start
          ctx.stroke();

        } else if (drawing.type === DRAWING_TOOLS.ERASER) {
          // rule_reference: eraser replayed as filled destination-out circles at stored centres
          // date_of_change: 2026-05-25 - Stored as eraserPoints array of {x,y,radius} world coords
          if (!drawing.eraserPoints || drawing.eraserPoints.length === 0) { ctx.restore(); return; }
          ctx.globalCompositeOperation = 'destination-out';
          drawing.eraserPoints.forEach((ep) => {
            const sc = adj(ep);
            const r = (ep.radius || drawing.lineWidth || 3) * zoom / 2;
            ctx.beginPath();
            ctx.arc(sc.x, sc.y, r, 0, 2 * Math.PI);
            ctx.fill();
          });

        } else if (drawing.type === DRAWING_TOOLS.LINE) {
          if (!drawing.startPoint || !drawing.endPoint) { ctx.restore(); return; }
          const s = adj(drawing.startPoint);
          const e = adj(drawing.endPoint);
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(e.x, e.y);
          ctx.stroke();

        } else if (drawing.type === DRAWING_TOOLS.SQUARE) {
          if (!drawing.startPoint || !drawing.endPoint) { ctx.restore(); return; }
          const s = adj(drawing.startPoint);
          const e = adj(drawing.endPoint);
          ctx.rect(s.x, s.y, e.x - s.x, e.y - s.y);
          ctx.stroke();

        } else if (drawing.type === DRAWING_TOOLS.CIRCLE) {
          if (!drawing.startPoint || !drawing.endPoint) { ctx.restore(); return; }
          const s = adj(drawing.startPoint);
          const e = adj(drawing.endPoint);
          const radius = Math.sqrt(Math.pow(e.x - s.x, 2) + Math.pow(e.y - s.y, 2));
          ctx.arc(s.x, s.y, radius, 0, 2 * Math.PI);
          ctx.stroke();
        }

        ctx.restore();
      });

      // Restore current settings
      ctx.strokeStyle = color;
      ctx.lineWidth   = lineWidth;
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Failed to redraw canvas paths'
        }
      );
    }
  }, [drawings, zoom, mapPosition, color, lineWidth]);

  // Get offset coordinates from mouse event
  const getOffsetCoordinates = useCallback((e) => {
    try {
      if (!canvasRef.current) return { offsetX: 0, offsetY: 0 };
      
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top
      };
    } catch (error) {
      handleError(
        error,
        false,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Failed to get offset coordinates'
        }
      );
      return { offsetX: 0, offsetY: 0 };
    }
  }, []);

  // Get raw canvas coordinates (for freehand/eraser - like fog lasso, no grid snap)
  const getRawCoordinates = useCallback((e) => {
    const { offsetX, offsetY } = getOffsetCoordinates(e);
    // Adjust for zoom and map position only (no grid snapping)
    return {
      x: (offsetX - mapPosition.x) / zoom,
      y: (offsetY - mapPosition.y) / zoom
    };
  }, [getOffsetCoordinates, zoom, mapPosition]);

  // Get adjusted coordinates considering zoom, map position, and grid snapping (for shapes)
  const getAdjustedCoordinates = useCallback((e) => {
    const { offsetX, offsetY } = getOffsetCoordinates(e);
    // Adjust for zoom and map position
    const adjustedX = (offsetX - mapPosition.x) / zoom;
    const adjustedY = (offsetY - mapPosition.y) / zoom;
    // Snap to grid if enabled
    return {
      x: snapToGrid ? Math.round(adjustedX / gridSize) * gridSize : adjustedX,
      y: snapToGrid ? Math.round(adjustedY / gridSize) * gridSize : adjustedY
    };
  }, [getOffsetCoordinates, zoom, mapPosition, snapToGrid, gridSize]);

  // Erase at a specific point - circular brush that instantly erases
  // date_of_change: 2026-05-25 - Circular eraser that erases instantly at cursor position
  const eraseAtPoint = useCallback((coords) => {
    try {
      if (!contextRef.current) return;

      const ctx = contextRef.current;
      ctx.save();
      ctx.beginPath();
      ctx.globalCompositeOperation = 'destination-out';
      // Draw a circle that clears pixels
      const screenX = coords.x * zoom + mapPosition.x;
      const screenY = coords.y * zoom + mapPosition.y;
      const radius = (lineWidth * zoom) / 2;
      ctx.arc(screenX, screenY, radius, 0, 2 * Math.PI);
      ctx.fill(); // Fill the circle with destination-out to erase
      ctx.restore();
    } catch (error) {
      handleError(error, false, { category: ERROR_CATEGORY.GENERAL, message: 'Error erasing at point' });
    }
  }, [lineWidth, zoom, mapPosition]);

  // Draw eraser cursor preview - shows a circle at current position (like a brush cursor)
  // date_of_change: 2026-05-25 - Circular eraser cursor preview
  const drawEraserCursor = useCallback((coords) => {
    try {
      if (!contextRef.current) return;

      // Transform world coordinates to screen coordinates
      const screenX = coords.x * zoom + mapPosition.x;
      const screenY = coords.y * zoom + mapPosition.y;
      const radius = (lineWidth * zoom) / 2;

      const ctx = contextRef.current;
      ctx.save();
      ctx.beginPath();
      ctx.arc(screenX, screenY, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.restore();
    } catch (error) {
      handleError(error, false, { category: ERROR_CATEGORY.GENERAL, message: 'Error drawing eraser cursor' });
    }
  }, [lineWidth, zoom, mapPosition]);

  // Handle mouse down event
  const handleMouseDown = useCallback((e) => {
    try {
      if (!enabled || !contextRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      // Freehand/eraser use raw coordinates (like fog lasso - no grid snap)
      // Shapes use adjusted coordinates (with optional grid snap)
      const isFreehandOrEraser = drawingTool === DRAWING_TOOLS.FREEHAND || drawingTool === DRAWING_TOOLS.ERASER;
      const coords = isFreehandOrEraser ? getRawCoordinates(e) : getAdjustedCoordinates(e);
      setIsDrawing(true);
      setStartPoint(coords);

      if (drawingTool === DRAWING_TOOLS.FREEHAND) {
        // rule_reference: freehand tracks raw points for smooth curves (like fog lasso)
        // date_of_change: 2026-05-25 - Freehand uses raw coords, no grid snapping
        setCurrentPath([coords]);
      } else if (drawingTool === DRAWING_TOOLS.ERASER) {
        // Start accumulating eraser points — first point on mousedown
        // date_of_change: 2026-05-25 - Eraser tracks circles committed on mouseup
        currentEraserPoints.current = [{ ...coords, radius: lineWidth }];
        redrawAllPaths();
        drawEraserCursor(coords);
      }

      lastPosition.current = coords;
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error in mouse down handler'
        }
      );
    }
  }, [enabled, getRawCoordinates, getAdjustedCoordinates, drawingTool, lineWidth, redrawAllPaths, drawEraserCursor]);

  // Draw freehand preview - shows polygon forming with line back to start (like fog lasso)
  // date_of_change: 2026-05-24 - Live preview for freehand tool (works like fog lasso)
  const drawFreehandPreview = useCallback((points) => {
    try {
      if (!contextRef.current || points.length < 2) return;

      // Transform world coordinates to screen coordinates
      const toScreen = (pt) => ({
        x: pt.x * zoom + mapPosition.x,
        y: pt.y * zoom + mapPosition.y
      });

      const ctx = contextRef.current;
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = color;
      // Make preview more visible: slightly wider with prominent dash
      ctx.lineWidth = Math.max(lineWidth + 1, 3);
      ctx.setLineDash([8, 4]); // Prominent dash pattern for preview
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const first = toScreen(points[0]);
      ctx.moveTo(first.x, first.y);

      // Draw path so far
      for (let i = 1; i < points.length; i++) {
        const p = toScreen(points[i]);
        ctx.lineTo(p.x, p.y);
      }

      // Open line preview - no closing back to start
      // date_of_change: 2026-05-25 - Freehand is open line, not closed polygon
      ctx.stroke();
      ctx.restore();
    } catch (error) {
      handleError(error, false, { category: ERROR_CATEGORY.GENERAL, message: 'Error drawing lasso preview' });
    }
  }, [color, lineWidth, zoom, mapPosition]);

  // Handle mouse move event
  const handleMouseMove = useCallback((e) => {
    try {
      if (!enabled || !contextRef.current || !isDrawing) return;

      e.preventDefault();
      e.stopPropagation();

      // Freehand/eraser use raw coordinates (like fog lasso - no grid snap)
      // Shapes use adjusted coordinates (with optional grid snap)
      const isFreehandOrEraser = drawingTool === DRAWING_TOOLS.FREEHAND || drawingTool === DRAWING_TOOLS.ERASER;
      const coords = isFreehandOrEraser ? getRawCoordinates(e) : getAdjustedCoordinates(e);

      if (drawingTool === DRAWING_TOOLS.FREEHAND) {
        // Freehand: collect raw points and show live preview (like fog lasso)
        // date_of_change: 2026-05-25 - Uses raw coords, no grid snapping for smooth curves
        const newPath = [...currentPath, coords];
        setCurrentPath(newPath);
        // Show freehand preview - redraw all first, then overlay current path
        // date_of_change: 2026-05-25 - Call directly like shapes do, not via requestAnimationFrame
        redrawAllPaths();
        drawFreehandPreview(newPath);
      } else if (drawingTool === DRAWING_TOOLS.ERASER) {
        // Accumulate point, redraw stored drawings + in-progress erase circles, then cursor
        // date_of_change: 2026-05-25 - Stateful eraser: replay all stored + current stroke via redraw
        currentEraserPoints.current = [...currentEraserPoints.current, { ...coords, radius: lineWidth }];
        // Replay all committed drawings (which includes past eraser strokes)
        redrawAllPaths();
        // Replay the in-progress eraser stroke directly so we see it live
        const ctx = contextRef.current;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        currentEraserPoints.current.forEach((ep) => {
          const sx = ep.x * zoom + mapPosition.x;
          const sy = ep.y * zoom + mapPosition.y;
          const r = (ep.radius || lineWidth) * zoom / 2;
          ctx.beginPath();
          ctx.arc(sx, sy, r, 0, 2 * Math.PI);
          ctx.fill();
        });
        ctx.restore();
        drawEraserCursor(coords);
      } else if (drawingTool === DRAWING_TOOLS.LINE ||
                 drawingTool === DRAWING_TOOLS.SQUARE ||
                 drawingTool === DRAWING_TOOLS.CIRCLE) {
        setEndPoint(coords);
        // Redraw with preview
        redrawAllPaths();
        drawPreview(startPoint, coords);
      }

      lastPosition.current = coords;
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error in mouse move handler'
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- drawPreview/drawFreehandPreview/drawEraserCursor defined after; no stale closure risk
  }, [enabled, isDrawing, getRawCoordinates, getAdjustedCoordinates, drawingTool, startPoint, currentPath, redrawAllPaths, eraseAtPoint, drawEraserCursor]);

  // Handle mouse up event
  const handleMouseUp = useCallback((e) => {
    try {
      if (!enabled || !isDrawing) return;
      
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      setIsDrawing(false);
      
      // date_of_change: 2026-05-22 — reset composite op after eraser stroke so normal tools work
      if (contextRef.current) {
        contextRef.current.globalCompositeOperation = 'source-over';
      }

      if (drawingTool === DRAWING_TOOLS.FREEHAND && currentPath.length > 0) {
        // date_of_change: 2026-05-24 - Freehand stores as open line
        const newDrawing = {
          type: drawingTool,
          points: [...currentPath],
          color,
          lineWidth
        };
        if (externalDrawings === null) setInternalDrawings(prev => [...prev, newDrawing]);
        if (onDrawingComplete) onDrawingComplete(newDrawing);
      } else if (drawingTool === DRAWING_TOOLS.ERASER) {
        // Commit the accumulated eraser stroke as a drawing so redrawAllPaths can replay it
        // date_of_change: 2026-05-25 - Eraser stored as eraserPoints array for persistent replay
        if (currentEraserPoints.current.length > 0) {
          const newDrawing = {
            type: DRAWING_TOOLS.ERASER,
            eraserPoints: [...currentEraserPoints.current],
            lineWidth,
            color: 'eraser'
          };
          currentEraserPoints.current = [];
          if (externalDrawings === null) setInternalDrawings(prev => [...prev, newDrawing]);
          if (onDrawingComplete) onDrawingComplete(newDrawing);
        }
      } else if ((drawingTool === DRAWING_TOOLS.LINE ||
                  drawingTool === DRAWING_TOOLS.SQUARE ||
                  drawingTool === DRAWING_TOOLS.CIRCLE) &&
                 startPoint && endPoint) {
        const newDrawing = {
          type: drawingTool,
          startPoint,
          endPoint,
          color,
          lineWidth
        };
        if (externalDrawings === null) setInternalDrawings(prev => [...prev, newDrawing]);
        if (onDrawingComplete) onDrawingComplete(newDrawing);
      }
      
      setCurrentPath([]);
      setStartPoint(null);
      setEndPoint(null);
    } catch (error) {
      handleError(
        error,
        false,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error in mouse up handler'
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- externalDrawings captured via closure intentionally
  }, [enabled, isDrawing, drawingTool, currentPath, startPoint, endPoint, color, lineWidth, onDrawingComplete, externalDrawings]);
  
  // Draw preview for shape tools
  // date_of_change: 2026-05-24 - Apply zoom transformation so preview matches final shape
  const drawPreview = useCallback((start, end) => {
    try {
      if (!contextRef.current || !start || !end) return;

      // Transform world coordinates to screen coordinates (same as redrawAllPaths)
      const toScreen = (pt) => ({
        x: pt.x * zoom + mapPosition.x,
        y: pt.y * zoom + mapPosition.y
      });

      const s = toScreen(start);
      const e = toScreen(end);

      contextRef.current.beginPath();
      contextRef.current.strokeStyle = color;
      contextRef.current.lineWidth = lineWidth;

      if (drawingTool === DRAWING_TOOLS.LINE) {
        contextRef.current.moveTo(s.x, s.y);
        contextRef.current.lineTo(e.x, e.y);
      } else if (drawingTool === DRAWING_TOOLS.SQUARE) {
        const width = e.x - s.x;
        const height = e.y - s.y;
        contextRef.current.rect(s.x, s.y, width, height);
      } else if (drawingTool === DRAWING_TOOLS.CIRCLE) {
        const radius = Math.sqrt(Math.pow(e.x - s.x, 2) + Math.pow(e.y - s.y, 2));
        contextRef.current.arc(s.x, s.y, radius, 0, 2 * Math.PI);
      }

      contextRef.current.stroke();
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error drawing preview'
        }
      );
    }
  }, [color, lineWidth, drawingTool, zoom, mapPosition]);
  
  // Handle mouse leave event
  const handleMouseLeave = useCallback(() => {
    try {
      if (isDrawing) {
        handleMouseUp();
      }
    } catch (error) {
      handleError(
        error,
        false,
        { 
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error in mouse leave handler'
        }
      );
    }
  }, [isDrawing, handleMouseUp]);
  
  // Update canvas when zoom or map position changes
  useEffect(() => {
    redrawAllPaths();
  }, [zoom, mapPosition, redrawAllPaths]);
  
  return (
    <canvas
      ref={canvasRef}
      className={`drawing-canvas absolute top-0 left-0 w-full h-full pointer-events-${enabled ? 'auto' : 'none'} z-20`}
      style={{ 
        cursor: enabled ? (drawingTool === 'freehand' ? 'crosshair' : 'crosshair') : 'default'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    />
  );
};

DrawingCanvas.propTypes = {
  enabled: PropTypes.bool,
  color: PropTypes.string,
  lineWidth: PropTypes.number,
  mapRef: PropTypes.object,
  zoom: PropTypes.number,
  mapPosition: PropTypes.object,
  onDrawingComplete: PropTypes.func,
  drawings: PropTypes.array,
  clearOnDisable: PropTypes.bool,
  snapToGrid: PropTypes.bool,
  gridSize: PropTypes.number,
  drawingTool: PropTypes.oneOf(['freehand', 'line', 'square', 'circle', 'text', 'polygon', 'eraser'])
};

export default DrawingCanvas;
